/**
 * Crypto Clicker 게임 코어 익스포트
 * 모든 게임 로직의 진입점
 */

import { GameCore } from '@app-games/core';
import { createInitialState } from './state.js';
import { actionHandlers } from './actions.js';
import { marketSentimentSystem } from './systems/marketSentimentSystem.js';
import { whaleSystem } from './systems/whaleSystem.js';
import { coinNewsSystem } from './systems/coinNewsSystem.js';
import { commentSystem } from './systems/commentSystem.js';
import { leadRoomSystem } from './systems/leadRoomSystem.js';
import { chatterSystem } from './systems/chatterSystem.js';
import { scenarioSystem } from './systems/scenarioSystem.js';
import { rugPullSystem } from './systems/rugPullSystem.js';
import { listingSystem } from './systems/listingSystem.js';
import { exchangeMarketSystem } from './systems/exchangeMarketSystem.js';
import { darkPoolSystem } from './systems/darkPoolSystem.js';
import { autoProductionSystem } from './systems/autoProductionSystem.js';
import { goldenEventSystem } from './systems/goldenEventSystem.js';
import { comboSystem } from './systems/comboSystem.js';
import { boosterSystem } from './systems/boosterSystem.js';
import { achievementSystem } from './systems/achievementSystem.js';
import { leaderboardSystem } from './systems/leaderboardSystem.js';
import { playTimeSystem } from './systems/playTimeSystem.js';
import type { GameState } from './state.js';
import type { GameAction } from './actions.js';

export type { GameState } from './state.js';
export type { GameAction } from './actions.js';

/**
 * 게임 코어 생성
 * @param debug 디버그 모드 활성화
 */
export function createGameCore(debug = false) {
  const core = new GameCore<GameState, GameAction>({
    createInitialState,
    debug,
    historySize: 100
  });

  // 액션 핸들러 등록
  for (const [type, handler] of Object.entries(actionHandlers)) {
    core.registerAction(type as GameAction['type'], handler as any);
  }

  // 시스템 등록 (순서 중요)
  core.registerSystem(playTimeSystem);            // 플레이 시간 추적
  core.registerSystem(marketSentimentSystem);      // 마켓 센티먼트 (CPS 배율에 영향)
  core.registerSystem(whaleSystem);                // 세력(고래) 은닉 사이클 (밈코인)
  core.registerSystem(coinNewsSystem);             // 코인 타겟 뉴스 + 찌라시 파이프라인
  core.registerSystem(commentSystem);              // 뉴스 댓글 NPC 반응 배달 + 트리밍 (Sprint 3a)
  core.registerSystem(leadRoomSystem);             // 가짜 리딩방 콜 생성/판정 + 정보상 힌트 만료 (Sprint 3a)
  core.registerSystem(chatterSystem);              // 리딩방 라이브 채팅 + 소문(진위) + 소문→시세 예약 (Sprint 3b)
  core.registerSystem(scenarioSystem);             // 변동성 시나리오 배정 (밈코인, Sprint 2b)
  core.registerSystem(rugPullSystem);              // 러그풀 이벤트 + 가격 샘플링 (Sprint 2b)
  core.registerSystem(listingSystem);              // 상장/상폐/부활 라이프사이클 (살아있는 시장)
  core.registerSystem(exchangeMarketSystem);       // 거래소 코인별 시세 + 캔들 (모든 편향 합산)
  core.registerSystem(darkPoolSystem);              // 다크풀 경매 (라운드 진행)
  core.registerSystem(autoProductionSystem);       // 자동 생산 (CPS + 자동 탭)
  core.registerSystem(goldenEventSystem);          // 골든 이벤트 출현/만료
  core.registerSystem(comboSystem);                // 콤보 감쇠
  core.registerSystem(boosterSystem);              // 부스터 만료
  core.registerSystem(achievementSystem);          // 업적 달성 체크
  core.registerSystem(leaderboardSystem);          // 자산 리더보드 (NPC 성장 + 플레이어 최고 순위)

  return core;
}
